import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { AtSign, Link as LinkIcon, LogOut, Mail, Trash2, TriangleAlert, UserMinus, UserPlus } from 'lucide-react'
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  CopyField,
  Form,
  IconButton,
  Notice,
  Skeleton,
  SkeletonGroup,
  TextField,
  copyTextFrom,
  toast,
} from '@/components/play'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import { useEventHosts } from '@/components/workspace/useEventHosts'
import { useWorkspaceExit, type WorkspaceExit } from '@/components/workspace/useWorkspaceExit'
import { expiresIn, hostsError, managerInviteError, managerInviteTarget } from '@/lib/hosts/api'
import type { Manager, ManagerInvitation, ManagerInviteTarget } from '@/lib/hosts/types'
import { personToneKey } from '@/lib/profile'
import { useAuth } from '@/state/AuthContext'
import type { EventResource } from '@/types/events'

const newestFirst = (a: { created_at: string }, b: { created_at: string }) => Date.parse(b.created_at) - Date.parse(a.created_at)

/** A confirmed change to the team: what the dialog says, what it does, and what to say after. */
type TeamChange = {
  title: string
  description: string
  confirmLabel: string
  run: () => Promise<void>
  failure: string
  done: () => void | Promise<void>
}

/**
 * The workspace's Team view (`/events/:id/team`; docs/features/event-managers.md, docs/features/play-event-workspace.md):
 * the event's managers, who run the whole event with its organizers.
 *
 * - **The team:** each manager with **Remove**, or **Leave** on your own row; then pending invitations, each with whom
 *   it is for (an address, an account's photo and @handle, or Link for a link anyone may take), when it expires and
 *   **Revoke** (and its link to copy where this page made it).
 * - **Email or @username** with **Invite a manager**, which the API emails the link from; and **Copy a manager link**,
 *   a single-use link for 48 hours that whoever opens it first takes.
 *
 * Only those the event says may (`can_manage_managers`: the organization's side) invite, revoke and remove managers,
 * see addresses, and hear "<name> joined as a manager"; a manager sees the team and Leave. A manager who leaves, or
 * whom an organizer removes (the pushed list no longer has them), goes to their events through `exit` (the
 * workspace's, shared with its channel's `access.revoked`). Renders nothing where the hosts endpoint answers 404.
 */
export function EventTeam({ event, exit: workspaceExit }: { event: EventResource; exit?: WorkspaceExit }) {
  const { user } = useAuth()
  const ownExit = useWorkspaceExit()
  const exit = workspaceExit ?? ownExit
  // A manager runs the event but leaves its team to the organization. An older API sends no flag: its access says.
  const managesTeam = event.attributes.can_manage_managers ?? event.attributes.access !== 'manager'
  const eventName = event.attributes.name
  const {
    managers,
    managerInvitations,
    loaded,
    loading,
    error,
    unavailable,
    load,
    inviteManager,
    createManagerLink,
    revokeManagerInvitation,
    removeManager,
  } = useEventHosts(event.id, {
    selfId: managesTeam ? null : user?.id,
    addresses: managesTeam,
    loadFailed: 'The team couldn’t load.',
    onSelfRemoved: () => exit.lost(eventName),
    onManagerJoined: (manager) => {
      if (managesTeam) toast({ title: `${manager.name} joined as a manager`, tone: 'success' })
    },
  })
  const headingId = useId()
  const field = useRef<HTMLInputElement>(null)
  // Set once a confirmed change succeeds, so focus moves to the invite field instead of the row that went.
  const done = useRef(false)
  const [change, setChange] = useState<TeamChange | null>(null)
  const [changeOpen, setChangeOpen] = useState(false)
  const [changeError, setChangeError] = useState('')

  if (unavailable) return null

  const finalFocus = () => (done.current ? field.current ?? true : true)

  function ask(next: TeamChange) {
    done.current = false
    setChange(next)
    setChangeError('')
    setChangeOpen(true)
  }

  async function confirmChange() {
    if (!change) return
    try {
      await change.run()
    } catch (failure) {
      setChangeError(hostsError(failure, change.failure))
      throw failure
    }
    done.current = true
    await change.done()
  }

  function askRemove(manager: Manager) {
    ask({
      title: `Remove ${manager.name}?`,
      description: 'They lose this event’s workspace.',
      confirmLabel: 'Remove manager',
      run: () => removeManager(manager.user_id),
      failure: `${manager.name} couldn’t be removed. Try again.`,
      done: () => void toast({ title: `Removed ${manager.name}` }),
    })
  }

  function askLeave(userId: string) {
    ask({
      title: `Leave ${eventName}?`,
      description: 'You lose its workspace. An organizer can invite you again.',
      confirmLabel: 'Leave event',
      run: async () => {
        // Losing access while leaving is expected: the API ends this account's workspace subscription as it goes.
        exit.leaving(true)
        try {
          await removeManager(userId)
        } catch (failure) {
          exit.leaving(false)
          throw failure
        }
      },
      failure: 'You couldn’t leave the event. Try again.',
      // The account as it is now first, then its events, or home for a viewer again.
      done: () => exit.left(eventName),
    })
  }

  function askRevoke(invitation: ManagerInvitation) {
    const link = invitation.binding === 'link'
    ask({
      title: link ? 'Revoke this manager link?' : `Revoke ${invitationName(invitation)}’s invite?`,
      description: 'Its link stops working right away.',
      confirmLabel: link ? 'Revoke link' : 'Revoke invite',
      run: () => revokeManagerInvitation(invitation.id),
      failure: 'The invite couldn’t be revoked. Try again.',
      done: () => void toast({ title: link ? 'Manager link revoked' : 'Invite revoked' }),
    })
  }

  return (
    <section className="pw-hosts pw-team" aria-labelledby={headingId}>
      <div className="pw-hosts-heading">
        <h2 id={headingId}>Team</h2>
        <WorkspaceInfo label="About the team">
          Your team runs this whole event with you: cameras, OBS setup, the Live show and hosts. Each invite works once:
          14 days by email or @username, 48 hours as a link.
        </WorkspaceInfo>
      </div>
      {!loaded ? (
        loading || !error ? (
          <SkeletonGroup label="Loading the team" className="pw-hosts-list">
            {[0, 1].map((row) => (
              <div key={row} className="pw-host">
                <Skeleton shape="circle" className="pw-host-skeleton-avatar" />
                <Skeleton className="pw-host-skeleton-text" />
              </div>
            ))}
          </SkeletonGroup>
        ) : (
          <Notice
            tone="error"
            action={<Button variant="secondary" size="compact" onClick={() => void load()}>Try again</Button>}
          >
            {error}
          </Notice>
        )
      ) : (
        <>
          {managers.length + managerInvitations.length > 0 ? (
            // role="list": Safari drops list semantics from lists styled without markers.
            <ul className="pw-hosts-list" role="list" aria-labelledby={headingId}>
              {managers.map((manager) => {
                const you = !!user && manager.user_id === user.id
                return (
                  <PersonRow
                    key={manager.user_id}
                    name={manager.name}
                    avatar={
                      <Avatar
                        name={manager.name}
                        src={manager.avatar_url}
                        tone="auto"
                        toneKey={personToneKey({ handle: manager.username, id: manager.user_id })}
                      />
                    }
                    meta={[manager.username ? `@${manager.username}` : manager.email, you ? 'You' : null]}
                    actions={
                      you ? (
                        <IconButton label="Leave this event" variant="ghost" onClick={() => askLeave(manager.user_id)}>
                          <LogOut />
                        </IconButton>
                      ) : managesTeam ? (
                        <IconButton label={`Remove ${manager.name}`} variant="ghost" onClick={() => askRemove(manager)}>
                          <UserMinus />
                        </IconButton>
                      ) : null
                    }
                  />
                )
              })}
              {[...managerInvitations].sort(newestFirst).map((invitation) => (
                <ManagerInvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  onRevoke={managesTeam ? () => askRevoke(invitation) : undefined}
                />
              ))}
            </ul>
          ) : (
            <p className="pw-hosts-empty">Your team runs this event with you.</p>
          )}
          {managesTeam && (
            <ManagerInviteForm field={field} eventName={eventName} invite={inviteManager} createLink={createManagerLink} />
          )}
        </>
      )}
      <ConfirmDialog
        open={changeOpen}
        onOpenChange={setChangeOpen}
        title={change?.title ?? ''}
        description={change?.description}
        confirmLabel={change?.confirmLabel ?? 'Confirm'}
        onConfirm={confirmChange}
        failureMessage={changeError || undefined}
        finalFocus={finalFocus}
      />
    </section>
  )
}

/** Whom a pending manager invitation names: its address (or "Email invite" where it isn't shown), its account's
 *  @handle or name, or a link's "Link". */
function invitationName(invitation: ManagerInvitation): string {
  if (invitation.binding === 'link') return 'Link'
  if (invitation.binding === 'account') {
    const { invitee } = invitation
    return invitee?.username ? `@${invitee.username}` : invitee?.name ?? 'Account invite'
  }
  return invitation.email ?? 'Email invite'
}

/**
 * A pending manager invitation: whom it is for, when it expires and Revoke (`onRevoke`, for those who may), with its
 * link to copy where this page made it. An address shows as itself (or "Email invite" without one); an account by its
 * photo, name and @handle; a link as Link, with a warning beside its link that whoever opens it first becomes a manager.
 */
function ManagerInvitationRow({ invitation, onRevoke }: { invitation: ManagerInvitation; onRevoke?: () => void }) {
  const { binding, invitee } = invitation
  const label = invitationName(invitation)
  const expiry = expiresIn(invitation.expires_at)
  let avatar: ReactNode
  let name: string
  let meta: ReactNode[]
  if (binding === 'link') {
    avatar = <span className="pw-invite-glyph" aria-hidden="true"><LinkIcon /></span>
    name = 'Link'
    meta = [<Badge key="once" tone="quiet" icon={<LinkIcon />}>One use</Badge>, expiry]
  } else if (binding === 'account' && invitee) {
    avatar = (
      <Avatar
        name={invitee.name}
        src={invitee.avatar_url}
        tone="auto"
        toneKey={personToneKey({ handle: invitee.username, id: invitee.user_id })}
      />
    )
    name = invitee.name
    meta = [invitee.username ? `@${invitee.username}` : null, <Badge key="invited" tone="quiet" icon={<Mail />}>Invited</Badge>, expiry]
  } else {
    avatar = invitation.email ? <Avatar name={invitation.email} /> : <span className="pw-invite-glyph" aria-hidden="true"><Mail /></span>
    name = label
    meta = [<Badge key="invited" tone="quiet" icon={<Mail />}>Invited</Badge>, expiry]
  }
  const link = binding === 'link'
  return (
    <li className="pw-host-invite">
      <PersonRow
        as="div"
        name={name}
        avatar={avatar}
        meta={meta}
        actions={
          onRevoke && (
            <IconButton label={link ? 'Revoke this manager link' : `Revoke ${label}’s invite`} variant="ghost" onClick={onRevoke}>
              <Trash2 />
            </IconButton>
          )
        }
      />
      {invitation.url && (
        <CopyField
          label={link ? 'Manager link' : 'Invite link'}
          hideLabel
          size="compact"
          value={invitation.url}
          copyLabel={link ? 'Copy manager link' : `Copy ${label}’s invite link`}
        />
      )}
      {invitation.url && link && (
        <p className="pw-invite-warning">
          <TriangleAlert aria-hidden="true" />
          Whoever opens it first becomes a manager.
        </p>
      )}
    </li>
  )
}

/**
 * Invite a manager: an email address, which the API sends the link to, or an account's @username, whose own address
 * the API emails it to (`managerInviteTarget` tells them apart by shape). A refusal about what was entered, or about
 * when to try again, stays beside the field; anything else shows under the form. Copy a manager link makes a
 * single-use link for 48 hours and copies it in the click; whoever opens it first becomes a manager, which the toast
 * and its row say. Each new invitation joins the list with its link to copy.
 */
function ManagerInviteForm({
  field,
  eventName,
  invite,
  createLink,
}: {
  field: RefObject<HTMLInputElement>
  eventName: string
  invite: (target: ManagerInviteTarget) => Promise<ManagerInvitation>
  createLink: () => Promise<ManagerInvitation & { url: string }>
}) {
  const [entry, setEntry] = useState('')
  const [sending, setSending] = useState(false)
  const [linking, setLinking] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  // Each invitation sent starts a fresh form, so the emptied field isn't flagged as missing; focus stays on it.
  const [sent, setSent] = useState(0)
  useEffect(() => {
    if (sent) field.current?.focus()
  }, [field, sent])

  async function submit() {
    const target = managerInviteTarget(entry)
    if (!target || sending) return
    setSending(true)
    setFieldError(null)
    setNotice('')
    try {
      const invitation = await invite(target)
      setEntry('')
      setSent((count) => count + 1)
      toast({ title: `Invite emailed to ${invitationName(invitation)}`, description: `To help run ${eventName}.`, tone: 'success' })
    } catch (failure) {
      const { field: message, notice: problem } = managerInviteError(failure, target)
      if (message) {
        setFieldError(message)
        field.current?.focus()
      }
      if (problem) setNotice(problem)
    } finally {
      setSending(false)
    }
  }

  async function copyLink() {
    if (linking) return
    setLinking(true)
    setNotice('')
    // Both start inside the click, so the clipboard keeps the click's permission while the link is made.
    const request = createLink()
    const copied = copyTextFrom(request.then((invitation) => invitation.url))
    try {
      await request
      if (await copied) {
        toast({ title: 'Manager link copied', description: 'One use · 48 hours. Whoever opens it first becomes a manager.', tone: 'success' })
      } else {
        toast({ title: 'Manager link ready', description: 'Copy it from the list. Whoever opens it first becomes a manager.' })
      }
    } catch (failure) {
      const { notice: problem } = managerInviteError(failure)
      setNotice(problem ?? 'A manager link couldn’t be made. Try again.')
    } finally {
      setLinking(false)
    }
  }

  return (
    <>
      <Form key={sent} className="pw-manager-form" onFormSubmit={() => void submit()}>
        <TextField
          ref={field}
          name="manager"
          type="text"
          inputMode="email"
          label="Email or @username"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="send"
          placeholder="jo@example.com or @jo"
          icon={<AtSign />}
          required
          requiredMessage="Enter an email or @username."
          value={entry}
          onChange={(change) => {
            setEntry(change.target.value)
            setFieldError(null)
          }}
          error={fieldError ?? undefined}
        />
        <div className="pw-manager-actions">
          <Button type="submit" variant="secondary" loading={sending}>
            <UserPlus />
            Invite a manager
          </Button>
          <Button type="button" variant="ghost" loading={linking} onClick={() => void copyLink()}>
            <LinkIcon />
            Copy a manager link
          </Button>
        </div>
      </Form>
      {notice && <Notice tone="error" alert>{notice}</Notice>}
    </>
  )
}

/** A person in the list: avatar, name, a quiet line of facts, and one action at the row's end. */
function PersonRow({
  as: Element = 'li',
  name,
  avatar,
  meta,
  actions,
}: {
  as?: 'li' | 'div'
  name: string
  avatar: ReactNode
  meta: ReactNode[]
  actions: ReactNode
}) {
  return (
    <Element className="pw-host">
      {avatar}
      <div className="pw-host-text">
        <span className="pw-host-name">{name}</span>
        <span className="pw-host-meta">
          {meta.filter(Boolean).map((item, index) => (typeof item === 'string' ? <span key={index}>{item}</span> : item))}
        </span>
      </div>
      <div className="pw-host-actions pw-host-actions--end">{actions}</div>
    </Element>
  )
}
